export type SourceKind = 'window' | 'screen'

interface Clock {
  later: (callback: () => void, delay: number) => ReturnType<typeof setTimeout>
  cancel: (timer: ReturnType<typeof setTimeout>) => void
}

/** Poll only while the picker is visible; serialize reads across tab/focus changes. */
export function watchSources<T>(read: (kind: SourceKind) => Promise<T>, receive: (value: T) => void,
  failed: () => void, clock: Clock = { later: (callback, delay) => setTimeout(callback, delay), cancel: (timer) => clearTimeout(timer) }) {
  let kind: SourceKind = 'window'
  let generation = 0
  let paused = true
  let stopped = false
  let pending = false
  let timer: ReturnType<typeof setTimeout> | undefined

  const clear = () => { if (timer !== undefined) clock.cancel(timer); timer = undefined }
  const refresh = async () => {
    clear()
    if (stopped || paused || pending) return
    pending = true
    const revision = generation
    try {
      const value = await read(kind)
      if (!stopped && !paused && revision === generation) receive(value)
    } catch {
      if (!stopped && !paused && revision === generation) failed()
    } finally {
      pending = false
      if (!stopped && !paused) {
        // A new tab or resumed picker must not wait for an old request's polling delay.
        if (revision !== generation) void refresh()
        else timer = clock.later(() => void refresh(), 5000)
      }
    }
  }

  return {
    kind(next: SourceKind) {
      if (next === kind) return
      kind = next
      generation++
      void refresh()
    },
    pause(next: boolean) {
      if (paused === next) return
      paused = next
      generation++
      clear()
      if (!paused) void refresh()
    },
    refresh() { generation++; void refresh() },
    stop() { stopped = true; clear() },
  }
}
